import { parseClientConfig, type ClientConfig } from '@ferro/config';

// Happy Tail Happy Dog — Denver boutique dog hotel & spa (kennel-free).
// Values mirror production/happy-tail/ but HTHD is not yet in live use
// with real users (imported customers are Gingr API test fixtures).
// Brand palette sourced from production/happy-tail/SKILL.md.
const config: ClientConfig = parseClientConfig({
  id: 'hthd',
  brand: {
    name: 'Happy Tail Happy Dog',
    tagline: 'Your furry family member deserves to feel at home.',
    colors: {
      primary: '#62A2C3',     // Brand Blue — headers, CTAs
      secondary: '#1B365D',   // Navy — text, accents
      accent: '#F5C65D',      // Golden Yellow — rewards, badges
      text: '#1B365D',
      background: '#F8F6F3',  // Warm White
    },
    fonts: {
      heading: 'Playfair Display',
      body: 'Open Sans',
    },
    // The prior value `/assets/logo.svg` did not exist (only `logo.png`
    // and `logo-full-color.png` ship in `clients/hthd/assets/`, and that
    // directory is not served by either app), so every consumer of this
    // static fallback got a 404. Point at the reliable, CDN-served copy
    // the customer-app publishes (`apps/customer-app/public/tenants/hthd/`),
    // confirmed HTTP 200 on Vercel and identical to the asset in
    // `clients/hthd/assets/logo-full-color.png`. This is the SAME logo the
    // invite email uses, so it stays in visual sync across surfaces.
    //
    // NOTE for the admin sidebar: this is the full-color logo on a
    // TRANSPARENT background designed for a light/cream card. It reads
    // poorly on the dark-navy admin sidebar (`--brand-secondary` #1B365D),
    // so the sidebar uses `logoDark` below instead (AppShell prefers it).
    logo: 'https://hthd.animalloversapp.com/tenants/hthd/logo-full-color.png',
    // Dark-surface variant, generated 2026-07-10 from the mark on
    // happytailhappydog.com (og:image full-color-c.png): white matte
    // un-blended, brown dog/script recolored to white, brand blue kept,
    // tagline cropped for small-size legibility (44px sidebar height).
    // Canonical art: clients/hthd/assets/logo-ondark.png (+ full lockup
    // logo-reversed.png); served copy published by the customer-app.
    logoDark: 'https://hthd.animalloversapp.com/tenants/hthd/logo-ondark.png',
    // Kate K13 — logo ROLES. `logo` above is the 1080x1080 full-color
    // square whose art fills 42% of its height; at the header's 48px that
    // left a ~20px mark ("the logo is staying like super micro tiny").
    // Headers and the landing hero read `logoHorizontal` (the 972x262
    // lockup: script wordmark + blue dog badge + tagline, canonical
    // clients/hthd/assets/logo.png); the favicon, PWA icon and tight slots
    // read `logoMark` (the dog badge cropped from the square, canonical
    // clients/hthd/assets/logo-mark.png). Served copies are published by
    // the customer-app. `logo` stays for callers that predate the roles.
    logoHorizontal: 'https://hthd.animalloversapp.com/tenants/hthd/logo-horizontal.png',
    logoMark: 'https://hthd.animalloversapp.com/tenants/hthd/logo-mark.png',
    logoOnDark: 'https://hthd.animalloversapp.com/tenants/hthd/logo-ondark.png',
  },
  vertical: 'pet-services',
  // 'staff-mgmt' deferred 2026-04-28 — module declared here but real
  // implementation does not exist (only stub at apps/server/src/stubs/
  // staff-schedules.ts). Tracked as QA-002 in docs/HTHD-PARITY-TRACKER.md
  // Phase 1 task #20. Re-add when packages/core/modules/staff-mgmt/ is built.
  modules: [
    'agreements',
    'auth',
    'booking',
    'crm',
    'loyalty',
    'messaging',
    'notifications',
    'payments',
  ],
  business: {
    services: ['daycare', 'boarding', 'grooming'],
    pricing: {
      // Ted-confirmed 2026-06-16/17 (#323). The authoritative charged price is
      // the ServiceType row (seed-hthd-catalog.ts); these mirror it for the
      // landing/marketing surface so quote == receipt (#272).
      daycare: 4700,
      boarding: 7000,
      // #272/#286 — website-published starting rate (Just A Bath, small),
      // 2026-07-23, pending Ted confirmation. DISPLAY-ONLY "from $48": grooming
      // is size-tiered and the real charge derives from the GroomingServicePrice
      // matrix (resolveGroomingUnitPriceCents), never this flat number. Was 6500
      // ($65) — a fabricated figure matching no published HTHD price.
      grooming: 4800,
    },
    locations: [
      { name: 'Denver', address: 'Denver, CO' },
    ],
    referralPrefix: 'HT',
  },
  loyalty: {
    earnRate: 1,
    serviceMultipliers: { grooming: 1.5 },
    redemptionTiers: { '100': 1000, '250': 2500, '500': 5000 },
  },
  deployment: {
    // Tenants on the platform all share the same Railway server; pickTenantConfig
    // overrides this at boot with the ALA platform URL when a tenant resolves.
    // The static value here is just the schema's required default — kept aligned
    // with ALA so a stale read of it still hits the live platform server.
    apiUrl: 'https://animalloversapp-platform-api-production.up.railway.app',
    customerAppUrl: 'https://hthd.animalloversapp.com',
    adminAppUrl: 'https://hthd.admin.animalloversapp.com',
  },
  integrations: {
    stripe: { mode: 'test' },
    openai: { model: 'gpt-4o' },
    pos: { provider: 'legacy-import' },
  },
  // #1212 texting B2a — show HTHD's clients the texting OPT-IN (the unchecked
  // sign-up box, the one-time sign-in card, the Notifications switches and the
  // Privacy/Terms SMS sections) so consent is recorded and the toll-free
  // registration for (844) 571-7307 can screenshot a real opt-in. Sends
  // nothing: `sms` stays off until registration clears. Marketing texts are
  // not in HTHD's registered use case, so `smsMarketingConsent` stays off.
  features: { smsConsentCapture: true },
});

export default config;
